import {
  Component,
  ViewChild,
  QueryList,
  ViewChildren,
  ElementRef,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  computed, effect, signal, OnDestroy
} from '@angular/core';
import { ModalController, Platform, IonRouterOutlet, IonicModule } from '@ionic/angular';
import { DishInformationPage } from '../dish-information/dish-information.page';
import { ICanteen, CanteenService, IDishDetails, IOpening } from '../canteen.service';
import { ActivatedRoute, Router, ParamMap } from '@angular/router';
import { CanteensPage } from '../canteens/canteens.page';
import { OverlayEventDetail } from '@ionic/core';
import { Browser } from '@capacitor/browser';
import { SwiperContainer } from 'swiper/element';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { OpeningsPipe } from '../openings.pipe';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';


interface IWeekday {
  id: number;
  de: string;
  en: string;
  dishes: IDishDetails[];
}

@Component({
    selector: 'app-menu',
    templateUrl: './menu.page.html',
    styleUrls: ['./menu.page.scss'],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [IonicModule, ReactiveFormsModule, FormsModule, TranslatePipe, OpeningsPipe]
})
export class MenuPage implements OnDestroy {
  canteenService = inject(CanteenService);
  private modalController = inject(ModalController);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private platform = inject(Platform);
  private routerOutlet = inject(IonRouterOutlet);

  @ViewChildren('weekdaySegment') weekdaySegment: QueryList<any>;
  @ViewChild('swiper') slides: ElementRef<SwiperContainer>;

  protected readonly canteenId = toSignal(
    this.route.paramMap.pipe(
      map(params => params.get('canteenId'))
    ),
    { initialValue: null }
  );

  protected readonly queryParams = toSignal(
    this.route.queryParamMap,
    { initialValue: null }
  );

  protected readonly canteen = computed(() => {
    const id = this.canteenId();
    const query = this.queryParams();

    if (!id || !query) return null;

    return {
      id,
      name: {
        de: query.get('de'),
        en: query.get('en'),
      },
    };
  });

  public weekdays = signal<IWeekday[]>([
    { id: 1, de: 'Mo', en: 'Mon', dishes: null },
    { id: 2, de: 'Di', en: 'Tue', dishes: null },
    { id: 3, de: 'Mi', en: 'Wed', dishes: null },
    { id: 4, de: 'Do', en: 'Thu', dishes: null },
    { id: 5, de: 'Fr', en: 'Fri', dishes: null },
  ]);

  protected nextOpeningTransition: Date;
  private openingTimeout?: ReturnType<typeof setTimeout>;
  protected isOpen = signal<boolean>(null);
  protected isIOS = signal<boolean>(this.platform.is('ios')).asReadonly();
  protected showLeftArrow = signal(false);
  protected showRightArrow = signal(false);
  protected isToday = computed<boolean>(() => this.selectedIndex() === this.todayIndex());
  protected todayWeekdayIndex = computed<number>(() => this.weekdays().findIndex((weekday: IWeekday) => weekday.id === MenuPage.today().getDay()));
  protected todayIndex = computed<number>(() => this.weekdays().findIndex((weekday: IWeekday) => weekday.id === new Date().getDay()));
  protected selectedIndex = signal<number>(this.todayWeekdayIndex())
  protected selectedWeekday = computed<IWeekday>(() => this.weekdays()[this.selectedIndex()]);

  constructor() {
    effect(() => {
      if (!this.canteenId()) {
        this.openCanteenSelection((selected: OverlayEventDetail<ICanteen>) => {
          if (selected.data) {
            this.navigationByCanteen(selected.data);
          }
        });
        return;
      }
    });
    effect(() => {
      if(!this.canteen()) {
        return;
      }
      const weekday = this.weekdays()[this.selectedIndex()];
      if(!weekday || weekday.dishes) {
        return;
      }
      this.canteenService
        .dishesByDate(this.canteen().id, this.dateFromWeekdayOfCurrentWeek(weekday.id))
        .subscribe((dishes: IDishDetails[]) => {
          const weekdays= this.weekdays();
          weekdays[this.selectedIndex()].dishes = dishes;
          this.weekdays.set(weekdays);
        });
    });
    this.route.data.subscribe((dishes: { dishes: IDishDetails[] }) => {
      this.weekdays()
        .filter((weekday: IWeekday) => MenuPage.today().getDay() === weekday.id)
        .forEach((weekday: IWeekday) => {
          weekday.dishes = dishes.dishes;
        });
    });
  }


  onSwipe(event) {
    this.selectedIndex.set(event.detail[0].activeIndex);
  }

  ionViewDidEnter() {
    const swiper = this.slides?.nativeElement?.swiper;
    swiper?.slideTo(this.selectedIndex(), 0);

    this.route.paramMap.subscribe((params: ParamMap) => {
      const canteenId = params.get('canteenId');
      this.canteenService.opening(canteenId).subscribe({
        next:(openings: IOpening): void => this.setOpenings(openings),
        error: (): void => this.isOpen.set(null),
      });
    });

    this.showRightArrow.set(this.weekdaySegment.first.el.scrollWidth > this.weekdaySegment.first.el.clientWidth);
    this.weekdaySegment.first.el.childNodes.forEach((element: HTMLIonSegmentButtonElement) => {
      if (!element.innerText || !this.selectedWeekday()) {
        return;
      }
      if (
        element.innerText.trim() === this.selectedWeekday().de.toUpperCase().trim() ||
        element.innerText.trim() === this.selectedWeekday().en.toUpperCase().trim()
      ) {
        element.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }
    });
  }

  ngOnDestroy() {
    clearTimeout(this.openingTimeout);
  }

  scrollHandler(event: any) {
    this.showLeftArrow.set(event.srcElement.scrollLeft > 0);
    const scrollLeftMax = event.srcElement.scrollWidth - event.srcElement.clientWidth - 1;
    this.showRightArrow.set(event.srcElement.scrollLeft < scrollLeftMax);
  }

  public hideImage(event: any): void {
    event.srcElement.parentElement.hidden = true;
  }

  public filterAdditives(input: string): string {
    const filteredAdditives = input.replace(/\s?\(((([0-9]+[a-z]?)+)[,]?)+\)/gi, '');
    return filteredAdditives.replace(/\s?,\s?/gi, ', ');
  }

  public openCanteenSelection(onDidDismissHandler: (canteen: OverlayEventDetail<ICanteen>) => void): void {
    this.modalController
      .create({
        component: CanteensPage,
        componentProps: {
          isModal: true,
        },
        canDismiss: true,
        presentingElement: this.routerOutlet.nativeEl
      })
      .then((modal: HTMLIonModalElement) => {
        modal.onDidDismiss().then(onDidDismissHandler);
        modal.present();
      });
  }

  public selectCanteen(): void {
    this.openCanteenSelection((selectedCateen: OverlayEventDetail<ICanteen>) => {
      if (selectedCateen.data !== undefined) {
        this.navigationByCanteen(selectedCateen.data);
      }
    });
  }

  public openDishInformation(): void {
    this.modalController
      .create({
        component: DishInformationPage,
        componentProps: {
          canteenId: this.canteen().id
        },
        canDismiss: true,
        presentingElement: this.routerOutlet.nativeEl
      })
      .then((modal: HTMLIonModalElement) => {
        modal.present();
      });
  }

  public openDishDetails(dish: IDishDetails): void {
    if (dish && dish.dispoId && dish.additives && dish.additives.length) {
      this.router.navigate(['details', dish.dispoId], { relativeTo: this.route, state: { dish } });
    }
  }

  public weekdaySelected(selectedWeekday: any): void {

    const eventTarget: any = selectedWeekday.target;
    const selectedWeekdayId = selectedWeekday.detail.value;
    this.selectedIndex.set(selectedWeekdayId);

    this.slides.nativeElement.swiper.slideTo(this.selectedIndex());

    if(eventTarget) {
      eventTarget.childNodes.forEach((element: HTMLIonSegmentButtonElement) => {
        if (element.textContent.includes(this.selectedWeekday().de) || element.textContent.includes(this.selectedWeekday().en)) {
          element.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
      });
    }

  }

  public openStwdoUrl() {
    const url = 'https://www.stwdo.de';
    Browser.open({ url });
  }

  private setOpenings(openings: IOpening): void {
    this.nextOpeningTransition = null;
    if (!openings) {
      this.isOpen.set(null);
      return;
    }

    const morningStartDate = this.canteenService.getDateFromTime(openings.morningStartTime);
    const morningEndDate = this.canteenService.getDateFromTime(openings.morningEndTime);
    const afternoonStartDate = this.canteenService.getDateFromTime(openings.afternoonStartTime);
    const afternoonEndDate = this.canteenService.getDateFromTime(openings.afternoonEndTime);
    const now = new Date();

    if (morningStartDate == null || morningEndDate == null) {
      this.isOpen.set(false);
    } else if(now.getTime() < morningStartDate.getTime()) {
      this.isOpen.set(false);
      this.nextOpeningTransition = morningStartDate;
    } else if(now.getTime() < morningEndDate.getTime()) {
      this.isOpen.set(true);
      this.nextOpeningTransition = morningEndDate;
    } else if(afternoonStartDate == null || afternoonEndDate == null) {
      this.isOpen.set(false);
    } else if(now.getTime() < afternoonStartDate.getTime()) {
      this.isOpen.set(false);
      this.nextOpeningTransition = afternoonStartDate;
    } else if(now.getTime() < afternoonEndDate.getTime()) {
      this.isOpen.set(true);
      this.nextOpeningTransition = afternoonEndDate;
    } else {
      this.isOpen.set(false);
    }

    if(this.nextOpeningTransition) {
      clearTimeout(this.openingTimeout);
      this.openingTimeout = setTimeout(() => {
        this.setOpenings(openings);
      }, this.nextOpeningTransition.getTime() - now.getTime());
    }
  }

  private static today(): Date {
    const today = new Date();
    if (today.getDay() === 6) {
      today.setDate(today.getDate() + 2);
    } else if (today.getDay() === 0) {
      today.setDate(today.getDate() + 1);
    }
    return today;
  }

  private navigationByCanteen(canteen: ICanteen): void {
    if (canteen) {
      this.router.navigate(['../', canteen.id], {
        relativeTo: this.route,
        replaceUrl: true,
        state: canteen,
        queryParams: { de: canteen.name.de, en: canteen.name.en },
      });
    }
  }

  private dateFromWeekdayOfCurrentWeek(weekday: number): Date {
    if ([0, 1, 2, 3, 4, 5, 6].indexOf(weekday) === -1) {
      return null;
    }
    const today = MenuPage.today();
    // Adjust when day is 0 (sunday)
    const diff = weekday === 0 ? today.getDate() + (7 - today.getDay()) : today.getDate() + (weekday - today.getDay());
    return new Date(today.setDate(diff));
  }
}
